import React, { useState } from 'react';
import stripeService from 'services/stripe';
import { formatCurrency } from 'utils/commonFunctions';

interface StripeRefundButtonProps {
  chargeId: string;
  maxAmount: number; // amount in cents
  currency: string;
  onRefundComplete?: () => void;
}

const StripeRefundButton: React.FC<StripeRefundButtonProps> = ({
  chargeId,
  maxAmount,
  currency,
  onRefundComplete
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [amountInCents, setAmountInCents] = useState<number>(maxAmount);
  const [reason, setReason] = useState<string>('requested_by_customer');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convert cents to dollars for display
  const amountInDollars = amountInCents / 100;
  const maxAmountInDollars = maxAmount / 100;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Convert input dollars to cents, ensuring we don't exceed maxAmount
    const newAmountInCents = Math.min(Math.round(Number(e.target.value) * 100), maxAmount);
    setAmountInCents(newAmountInCents);
  };

  const handleRefund = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await stripeService.refundCharge(chargeId, {
        amount: amountInCents, // Send amount in cents to the API
        reason,
      });
      setIsModalOpen(false);
      if (onRefundComplete) {
        onRefundComplete();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err?.message || 'Failed to process refund');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : 'Refund Charge'}
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />

            <div className="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Process Refund
                  </h3>
                  <div className="mt-4">
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                      Amount to Refund ({currency.toUpperCase()})
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        id="amount"
                        step="0.01"
                        min="0"
                        max={maxAmountInDollars}
                        value={amountInDollars}
                        onChange={handleAmountChange}
                        className="block w-full pl-7 pr-12 border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="0.00"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">{currency.toUpperCase()}</span>
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Maximum refund amount: {formatCurrency(maxAmount, currency)}
                    </p>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                      Reason for Refund
                    </label>
                    <select
                      id="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="requested_by_customer">Requested by customer</option>
                      <option value="duplicate">Duplicate charge</option>
                      <option value="fraudulent">Fraudulent</option>
                    </select>
                  </div>

                  {error && (
                    <div className="mt-4 text-sm text-red-600">
                      {error}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleRefund}
                  disabled={isLoading}
                  className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isLoading ? 'Processing...' : 'Process Refund'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isLoading}
                  className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StripeRefundButton; 